import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Search, X, ArrowRight, TrendingUp, Cpu, Globe, Calendar,
  Hash, LayoutDashboard, Building, BarChart3, Clock, ArrowUp, ArrowDown
} from 'lucide-react';
import { searchIndex, categoryConfig, SearchItem } from '../data/searchData';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (view: string) => void;
}

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  LayoutDashboard,
  TrendingUp,
  Building,
  Cpu,
  BarChart3,
  Clock,
  ArrowRight,
  Globe,
  Calendar,
  Hash,
};

const SearchModal: React.FC<Props> = ({ isOpen, onClose, onNavigate }) => {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Filter results based on query
  const results = useMemo(() => {
    if (!query.trim()) {
      // Show navigation items when no query
      return searchIndex.filter(item => item.category === 'NAVIGATION');
    }

    const lowerQuery = query.toLowerCase().trim();
    const words = lowerQuery.split(' ');

    return searchIndex
      .filter(item => {
        const searchText = [
          item.title,
          item.subtitle,
          ...item.keywords,
        ].join(' ').toLowerCase();

        return words.every(word => searchText.includes(word));
      })
      .sort((a, b) => {
        // Prioritize exact title matches
        const aTitle = a.title.toLowerCase();
        const bTitle = b.title.toLowerCase();
        if (aTitle.startsWith(lowerQuery) && !bTitle.startsWith(lowerQuery)) return -1;
        if (!aTitle.startsWith(lowerQuery) && bTitle.startsWith(lowerQuery)) return 1;

        // Then by category priority
        const categoryOrder = ['NAVIGATION', 'INDICATOR', 'SECTOR', 'COUNTRY', 'EVENT', 'METRIC'];
        return categoryOrder.indexOf(a.category) - categoryOrder.indexOf(b.category);
      })
      .slice(0, 12); // Limit results
  }, [query]);

  // Group results by category
  const groupedResults = useMemo(() => {
    const groups: Record<string, SearchItem[]> = {};
    results.forEach(item => {
      if (!groups[item.category]) {
        groups[item.category] = [];
      }
      groups[item.category].push(item);
    });
    return groups;
  }, [results]);

  // Flatten for keyboard navigation
  const flatResults = results;

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);

      // Load recent searches from localStorage
      try {
        const stored = localStorage.getItem('sr2-recent-searches');
        if (stored) {
          setRecentSearches(JSON.parse(stored));
        }
      } catch (e) {
        // Ignore localStorage errors
      }
    }
  }, [isOpen]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => Math.min(prev + 1, flatResults.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => Math.max(prev - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (flatResults[selectedIndex]) {
            handleSelect(flatResults[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, flatResults, selectedIndex, onClose]);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current) {
      const selectedEl = listRef.current.querySelector(`[data-index="${selectedIndex}"]`);
      selectedEl?.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  // Handle selection
  const handleSelect = (item: SearchItem) => {
    // Save to recent searches
    try {
      const updated = [item.title, ...recentSearches.filter(s => s !== item.title)].slice(0, 5);
      setRecentSearches(updated);
      localStorage.setItem('sr2-recent-searches', JSON.stringify(updated));
    } catch (e) {
      // Ignore localStorage errors
    }

    // Execute action
    if (item.action.type === 'navigate') {
      onNavigate(item.action.target);
    }

    onClose();
  };

  // Render category icon
  const renderIcon = (iconName?: string, className?: string) => {
    if (!iconName) return null;
    const Icon = iconMap[iconName];
    return Icon ? <Icon className={className || 'w-4 h-4'} /> : null;
  };

  // Get value badge style
  const getValueStyle = (value?: string | number): string => {
    if (!value) return 'bg-terminal-border text-gray-300';
    const v = String(value);
    if (v === 'STRONG BUY') return 'bg-data-green/20 text-data-green';
    if (v === 'BUY') return 'bg-data-green/10 text-data-green';
    if (v === 'HOLD') return 'bg-data-amber/20 text-data-amber';
    if (v === 'CRITICAL') return 'bg-data-red/20 text-data-red';
    if (v === 'HIGH') return 'bg-data-amber/20 text-data-amber';
    if (v === 'MEDIUM') return 'bg-gray-600/20 text-gray-400';
    return 'bg-terminal-border text-gray-300';
  };

  // Get change color
  const getChangeColor = (change?: string): string => {
    if (!change) return 'text-gray-400';
    if (change.startsWith('+') || change.startsWith('↑')) return 'text-data-green';
    if (change.startsWith('-') || change.startsWith('↓')) return 'text-data-red';
    return 'text-gray-400';
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/70 z-50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-x-0 top-0 md:top-[15%] md:left-1/2 md:-translate-x-1/2 md:w-full md:max-w-2xl z-50 px-0 md:px-4">
        <div className="bg-terminal-panel border-0 md:border border-terminal-border md:rounded-lg shadow-2xl overflow-hidden min-h-screen md:min-h-0">

          {/* Search Input */}
          <div className="flex items-center gap-2 md:gap-3 px-3 md:px-4 min-h-[56px] md:py-3 border-b border-terminal-border">
            <Search className="w-5 h-5 text-gray-500 shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setSelectedIndex(0);
              }}
              placeholder="Search indicators, sectors..."
              className="flex-1 bg-transparent text-white text-base md:text-sm outline-none placeholder-gray-500"
              autoComplete="off"
              spellCheck={false}
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="min-w-[44px] min-h-[44px] md:min-w-0 md:min-h-0 flex items-center justify-center md:p-1 hover:bg-terminal-border active:bg-terminal-border/80 rounded"
              >
                <X className="w-5 h-5 md:w-4 md:h-4 text-gray-500" />
              </button>
            )}
            <button
              onClick={onClose}
              className="min-w-[44px] min-h-[44px] md:min-w-0 md:min-h-0 flex items-center justify-center md:hidden text-gray-400 hover:text-white active:bg-terminal-border rounded"
              aria-label="Close search"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="hidden md:flex items-center gap-1 text-[10px] text-gray-600">
              <span className="px-1.5 py-0.5 bg-terminal-border rounded">ESC</span>
              <span>to close</span>
            </div>
          </div>

          {/* Results */}
          <div ref={listRef} className="max-h-[calc(100vh-120px)] md:max-h-[60vh] overflow-y-auto">
            {flatResults.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-500">
                <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No results found for "{query}"</p>
                <p className="text-xs mt-1">Try different keywords</p>
              </div>
            ) : (
              Object.entries(groupedResults).map(([category, items]) => (
                <div key={category}>
                  {/* Category Header */}
                  <div className="px-4 py-2 bg-terminal-header border-b border-terminal-border">
                    <span className={`text-[11px] md:text-[10px] font-medium uppercase tracking-wider ${categoryConfig[category]?.color || 'text-gray-400'}`}>
                      {categoryConfig[category]?.label || category}
                    </span>
                  </div>

                  {/* Items */}
                  {items.map((item) => {
                    const globalIndex = flatResults.indexOf(item);
                    const isSelected = globalIndex === selectedIndex;

                    return (
                      <div
                        key={item.id}
                        data-index={globalIndex}
                        onClick={() => handleSelect(item)}
                        className={`flex items-center gap-3 px-4 min-h-[52px] md:min-h-0 py-3 md:py-2.5 cursor-pointer transition-colors active:bg-terminal-header/80 ${
                          isSelected
                            ? 'bg-data-amber/10 border-l-2 border-data-amber'
                            : 'hover:bg-terminal-header border-l-2 border-transparent'
                        }`}
                      >
                        {/* Icon */}
                        <div className={`w-8 h-8 rounded flex items-center justify-center ${
                          isSelected ? 'bg-data-amber/20' : 'bg-terminal-border'
                        }`}>
                          {renderIcon(item.icon, `w-4 h-4 ${isSelected ? 'text-data-amber' : 'text-gray-400'}`)}
                          {!item.icon && (
                            <span className="text-xs text-gray-500">{item.category.charAt(0)}</span>
                          )}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-medium truncate ${isSelected ? 'text-white' : 'text-gray-200'}`}>
                              {item.title}
                            </span>
                            {item.value && (
                              <span className={`text-xs px-1.5 py-0.5 rounded ${getValueStyle(item.value)}`}>
                                {item.value}
                              </span>
                            )}
                          </div>
                          {item.subtitle && (
                            <p className="text-xs text-gray-500 truncate">{item.subtitle}</p>
                          )}
                        </div>

                        {/* Change indicator */}
                        {item.change && (
                          <div className={`text-xs font-medium ${getChangeColor(item.change)}`}>
                            {item.change}
                          </div>
                        )}

                        {/* Navigation hint */}
                        {isSelected && (
                          <div className="flex items-center gap-1 text-[10px] text-gray-500">
                            <span className="px-1 py-0.5 bg-terminal-border rounded">↵</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 md:py-2 border-t border-terminal-border bg-terminal-header">
            <div className="flex items-center justify-between text-[11px] md:text-[10px] text-gray-500">
              <div className="hidden md:flex items-center gap-3">
                <span className="flex items-center gap-1">
                  <ArrowUp className="w-3 h-3" />
                  <ArrowDown className="w-3 h-3" />
                  <span>Navigate</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className="px-1 py-0.5 bg-terminal-border rounded">↵</span>
                  <span>Select</span>
                </span>
              </div>
              <span className="md:hidden">Tap to select</span>
              <span>{flatResults.length} results</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SearchModal;
